import type { WizardStepperStage } from '@/components/setup/WizardStepper';

// Three stages only. There is no plugins stage and no plugin-setup stage:
// the create form derives the org's plugin set from the self-described
// onboarding profile, and the finish step points at Admin → Plugins for
// later changes. Model provider keys are not part of onboarding either —
// they live on Admin → Model Providers, which every org reaches after the
// wizard. The one credential onboarding must resolve is the Runloop
// sandbox account, because no agent can start without it.
export const ORG_SETUP_STAGES = ['organization', 'runloop', 'done'] as const;
export type OrgSetupStage = (typeof ORG_SETUP_STAGES)[number];

/** Always-visible final stage; also the clamp target for a stale URL. */
export const LAST_STAGE: OrgSetupStage = 'done';

/**
 * Existing-mode stage to fall back to when the URL has no `:stage` segment
 * yet (e.g. when an external link drops the user on
 * `/orgs/:orgSlug/setup/<bogus>`, including the retired `api-keys` stage).
 * The Runloop stage is the natural starting point because `mode="existing"`
 * already has a created org — the `organization` stage in that mode is an
 * edit form the rail marks as complete. The `runloop` stage is
 * *conditionally* shown: the per-org setup state (`sandboxConfigured`)
 * decides whether it appears, so an org that transferred its key at create
 * time skips it. When the URL lands on a skipped stage we snap forward to
 * the first remaining one.
 */
export const EXISTING_DEFAULT_STAGE: OrgSetupStage = 'runloop';

export const ALL_STAGES: ReadonlyArray<WizardStepperStage<OrgSetupStage>> = [
  { id: 'organization', label: 'Organization' },
  { id: 'runloop', label: 'Runloop account' },
  { id: 'done', label: 'Done' },
];

export function isOrgSetupStage(value: string | undefined): value is OrgSetupStage {
  return value !== undefined && (ORG_SETUP_STAGES as readonly string[]).includes(value);
}
